import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useImmer } from 'use-immer';
import { faWallet } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { shortenHash } from '@soundxyz/utils/const';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import { TooltipContextProvider } from '@components/main-components/tooltip/Context';
import { MediaQuery } from '@context/mediaQuery';
import { useToast } from '@context/toast';
import { LoadingSkeleton } from './basics/LoadingSkeleton';
import { Tooltip } from './main-components/tooltip/Tooltip';

export const CopyAddress = ({
  publicAddress,
  showIcon,
  className,
  loading,
}: {
  publicAddress: string;
  showIcon?: boolean;
  className?: string;
  loading?: boolean;
}) => {
  const [state, produce] = useImmer(() => ({
    addressCopied: false,
    tooltipOpen: false,
    isTouched: false,
  }));

  const latestState = useLatestRef(state);

  const { addressCopied, tooltipOpen } = state;

  const { tabletLandscape } = MediaQuery.useContainer();
  useEffect(() => {
    const resetCheckMark = setTimeout(() => {
      if (latestState.current.addressCopied === true) {
        produce(draft => {
          draft.addressCopied = false;

          if (latestState.current.isTouched) {
            draft.tooltipOpen = false;
            draft.isTouched = false;
          }
        });
      }
    }, 1000);

    return () => {
      clearTimeout(resetCheckMark);
    };
  }, [addressCopied, latestState, produce]);

  const publicAddrTruncated = shortenHash(publicAddress, 5, 4);

  const { openToast } = useToast();

  const setTooltipOpen = (open: boolean) =>
    produce(draft => {
      draft.tooltipOpen = open;
      draft.isTouched = true;
    });

  return (
    <TooltipContextProvider
      openOnClick={!tabletLandscape}
      openContent={tooltipOpen}
      setOpenContent={setTooltipOpen}
    >
      <Tooltip openOnClick={!tabletLandscape}>
        <Tooltip.Trigger className={clsx('cursor-pointer', className && className)} asChild>
          <div
            className="copy-trigger flex items-center gap-1.5"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(publicAddress);
              } catch (err) {
                openToast({
                  variant: 'error',
                  text: 'Copying to clipboard failed. Please check browser permissions',
                });
                return;
              }

              produce(draft => {
                draft.addressCopied = true;
                draft.tooltipOpen = true;
              });
            }}
          >
            {showIcon && (
              <FontAwesomeIcon icon={faWallet} className="mr-[2px] md:mx-[1px]" fontSize={20} />
            )}
            <LoadingSkeleton loading={!!loading} className="h-[18px] w-[88px]">
              <p className="publicAddr publicAddress margin-0 cursor-pointer">
                {publicAddrTruncated}
              </p>
            </LoadingSkeleton>
          </div>
        </Tooltip.Trigger>
        <Tooltip.StyledContent size="xs">
          {addressCopied === false ? 'Copy Address' : 'Address Copied'}
        </Tooltip.StyledContent>
      </Tooltip>
    </TooltipContextProvider>
  );
};
